import type { CameraName } from '@digger/shared';

/**
 * Dual-feed layout: both cameras render at all times — one full-frame
 * (dominant), the other picture-in-picture. Swapping is pure CSS class
 * work on already-playing elements, so it is instant (no renegotiation).
 */
export class VideoPanels {
  private readonly els: Record<CameraName, HTMLVideoElement>;

  constructor(front: HTMLVideoElement, rear: HTMLVideoElement) {
    this.els = { front, rear };
    for (const el of Object.values(this.els)) {
      el.muted = true;
      el.autoplay = true;
      el.playsInline = true;
    }
  }

  attachStream(camera: CameraName, stream: MediaStream): void {
    const el = this.els[camera];
    if (el.srcObject !== stream) {
      el.srcObject = stream;
      void el.play().catch(() => {
        /* autoplay policies; muted video will start on next interaction */
      });
    }
  }

  attachMjpeg(camera: CameraName, container: HTMLElement): void {
    // Debug transport: swap the <video> for an <img> MJPEG stream.
    const el = this.els[camera];
    const img = document.createElement('img');
    img.src = `/api/camera/${camera}/mjpeg`;
    img.className = el.className;
    img.dataset.camera = camera;
    container.replaceChild(img, el);
  }

  setDominant(dominant: CameraName): void {
    const pip: CameraName = dominant === 'front' ? 'rear' : 'front';
    this.applyClasses(dominant, pip);
  }

  private applyClasses(dominant: CameraName, pip: CameraName): void {
    for (const [name, cls] of [
      [dominant, 'feed dominant'],
      [pip, 'feed pip'],
    ] as const) {
      // Cover <img> replacements in mjpeg mode too.
      const el = document.querySelector<HTMLElement>(`[data-camera="${name}"]`) ?? this.els[name];
      el.className = cls;
    }
  }
}
